import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ADMIN_PATHS, adminPath } from '../../../constants/adminPaths';
import {
  AssessmentIcon,
  BusinessIcon,
  CorporateFareIcon,
  PersonIcon,
  SecurityIcon,
} from '../../icons';
import { adminGetAppUser, toUserDate } from '../../../api/appUsersApi';
import UserDetails from './UserDetails';
import UserSecurity from './UserSecurity';
import UserPackage from './UserPackage';
import UserUsageTracking from './UserUsageTracking';
import UserCompanyProfile from './UserCompanyProfile';
import UserSpecialAdmin from '../../../user-management/components/UserSpecialAdmin';
import { FormSkeleton } from '../../../../components/loading/skeletons';
import '../../../styles/pages/user-profile.css';

const UserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Derive a normalized status for consistent chip styling
  const isBlocked = user?.status === 'blocked';
  const hasLastActive = Boolean(user?.lastActiveAt);
  const derivedStatus = isBlocked
    ? 'blocked'
    : !hasLastActive
      ? 'inactive'
      : (user?.status || 'active');
  const statusLabel =
    derivedStatus.charAt(0).toUpperCase() + derivedStatus.slice(1);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const fetched = await adminGetAppUser(userId);
        if (fetched) {
          setUser(fetched);
        } else {
          setError('User not found');
        }
      } catch (err) {
        setError('Error fetching user data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId]);

  if (loading) {
    return (
      <div className="admin-page">
        <FormSkeleton fields={6} label="Loading user" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <p className="admin-alert">{error}</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="user-profile-header-bar">
        <div className="user-profile-header-top">
          <button
            type="button"
            className="admin-btn admin-btn-secondary"
            onClick={() => navigate(adminPath(ADMIN_PATHS.USERS))}
          >
            Back to Users
          </button>
          <span className="user-profile-breadcrumb">User Profile</span>
        </div>

        <div className="admin-card user-profile-card">
          <div className="user-profile-main">
            {user?.photoURL ? (
              <img className="user-profile-avatar" src={user.photoURL} alt="" />
            ) : (
              <span className="user-profile-avatar user-profile-avatar--fallback">
                {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase()}
              </span>
            )}

            <div className="user-profile-meta">
              <div className="user-profile-name-row">
                <span className="user-profile-name">
                  {user?.firstName} {user?.lastName}
                </span>
                <span className={`badge user-status-chip user-status-chip--${derivedStatus}`}>
                  {statusLabel}
                </span>
                {user?.package ? (
                  <span className="badge badge-default">{user.package.name}</span>
                ) : null}
              </div>

              <span className="user-profile-email">
                {user?.email}
              </span>

              <span className="user-profile-meta-row">
                Member since: {toUserDate(user?.createdAt)?.toLocaleDateString() || '—'}
              </span>
              <span className="user-profile-meta-row">
                Last active: {toUserDate(user?.lastActiveAt)?.toLocaleString() || '—'}
              </span>
            </div>
          </div>

          <div className="user-profile-tabs">
            <div className="user-profile-tab-row">
              {[
                { label: 'Details', icon: <PersonIcon /> },
                { label: 'Security', icon: <SecurityIcon /> },
                { label: 'Package', icon: <BusinessIcon /> },
                { label: 'Company Profile', icon: <CorporateFareIcon /> },
                { label: 'Usage', icon: <AssessmentIcon /> }
              ].map((tab, index) => (
                <button
                  key={tab.label}
                  type="button"
                  className={`user-profile-tab ${
                    activeTab === index ? 'user-profile-tab--active' : ''
                  }`}
                  onClick={() => setActiveTab(index)}
                >
                  <span className="user-profile-tab-icon">{tab.icon}</span>
                  <span className="user-profile-tab-label">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <UserSpecialAdmin user={user} setUser={setUser} />
        {activeTab === 0 && <UserDetails user={user} setUser={setUser} />}
        {activeTab === 1 && <UserSecurity user={user} setUser={setUser} />}
        {activeTab === 2 && <UserPackage user={user} setUser={setUser} />}
        {activeTab === 3 && <UserCompanyProfile user={user} />}
        {activeTab === 4 && <UserUsageTracking user={user} />}
      </div>
    </div>
  );
};

export default UserProfile; 